/**
 * Tiny inline SVG sparkline. No chart library — pure SVG.
 *
 * Props:
 *   values: number[]  → series of values (left = oldest, right = newest)
 *   width:  number    → SVG width in px (default 160)
 *   height: number    → SVG height in px (default 40)
 *   color:  string    → stroke color (default teal #00E5CC)
 *
 * Renders a smoothed polyline + a filled area underneath + dots at each point.
 * Last point is highlighted with a slightly larger ring.
 */
export const Sparkline = ({
  values = [],
  width = 160,
  height = 40,
  color = '#00E5CC',
  'data-testid': testId,
}) => {
  if (!values || values.length === 0) return null;

  const padding = 4;
  const innerW = width - padding * 2;
  const innerH = height - padding * 2;

  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;

  const stepX = values.length > 1 ? innerW / (values.length - 1) : 0;

  const points = values.map((v, i) => {
    const x = padding + i * stepX;
    const y = padding + innerH - ((v - min) / range) * innerH;
    return [x, y];
  });

  const linePath = points
    .map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`))
    .join(' ');

  const areaPath = `${linePath} L ${padding + (values.length - 1) * stepX} ${padding + innerH} L ${padding} ${padding + innerH} Z`;

  const last = points[points.length - 1];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      data-testid={testId}
      className="overflow-visible"
    >
      <defs>
        <linearGradient id="sparkline-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      <path d={areaPath} fill="url(#sparkline-fill)" />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="1.75"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {points.map(([x, y], i) => (
        <circle
          key={i}
          cx={x}
          cy={y}
          r={i === points.length - 1 ? 3 : 1.75}
          fill={color}
        />
      ))}
      {/* Subtle ring around the most recent point */}
      <circle cx={last[0]} cy={last[1]} r="5" fill="none" stroke={color} strokeOpacity="0.35" strokeWidth="1.5" />
    </svg>
  );
};

export default Sparkline;
